import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Delete, CheckCircle2, XCircle, Clock } from "lucide-react";
import axios from "axios";
import { KIOSK_PAGE } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/kiosk`;

export default function Kiosk() {
  const { token } = useParams();
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [deviceError, setDeviceError] = useState(null);
  const [input, setInput] = useState("");
  const [identified, setIdentified] = useState(null);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [now, setNow] = useState(new Date());
  const inputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/${token}/info`).then(({ data }) => setDeviceInfo(data)).catch(() => setDeviceError("Este dispositivo de kiosko no está activo o el enlace es inválido."));
  }, [token]);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { inputRef.current?.focus(); }, [identified, confirmation]);

  const reset = () => {
    setInput("");
    setIdentified(null);
    setError(null);
    setConfirmation(null);
  };

  const identify = async (value) => {
    if (!value) return;
    try {
      const payload = /^\d{4,6}$/.test(value) ? { pin: value } : { qr_token: value };
      const { data } = await axios.post(`${API}/${token}/identify`, payload);
      setIdentified(data);
      setInput("");
    } catch (err) {
      setError(err?.response?.data?.detail || "No se encontró a nadie con esos datos");
      setInput("");
      setTimeout(() => setError(null), 2500);
    }
  };

  const confirmPunch = async () => {
    try {
      const { data } = await axios.post(`${API}/${token}/punch`, { employee_id: identified.employee_id, method: identified.method });
      setConfirmation(data);
      setIdentified(null);
      setTimeout(reset, 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo registrar el marcaje");
      setIdentified(null);
      setTimeout(() => setError(null), 2500);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") identify(input.trim());
  };

  if (deviceError) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center text-center p-8">
        <p data-testid={KIOSK_PAGE.errorMessage} className="text-white/70 max-w-sm">{deviceError}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-charcoal flex flex-col items-center justify-center p-6 select-none">
      <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} className="opacity-0 absolute w-0 h-0" data-testid={KIOSK_PAGE.pinInput} autoFocus />

      <div className="absolute top-6 left-6 flex items-center gap-2 text-white/60">
        <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
        <span className="text-sm font-medium">{deviceInfo?.company_name}</span>
        {deviceInfo?.label && <span className="text-xs">· {deviceInfo.label}</span>}
      </div>
      <div className="absolute top-6 right-6 flex items-center gap-2 text-white/60 text-sm">
        <Clock className="w-4 h-4" strokeWidth={1.5} /> {now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
      </div>

      {confirmation ? (
        <div data-testid={KIOSK_PAGE.successMessage} className="text-center">
          <CheckCircle2 className="w-20 h-20 text-emerald-400 mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-white text-2xl font-heading font-bold">{confirmation.event_type === "entrada" ? "Entrada registrada" : "Salida registrada"}</p>
          <p className="text-white/60 mt-1">{confirmation.full_name}</p>
        </div>
      ) : error ? (
        <div data-testid={KIOSK_PAGE.errorMessage} className="text-center">
          <XCircle className="w-20 h-20 text-red-400 mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-white text-lg">{error}</p>
        </div>
      ) : identified ? (
        <div className="text-center">
          <p className="text-white/60 text-sm">Hola,</p>
          <p className="text-white text-3xl font-heading font-bold mt-1">{identified.full_name}</p>
          <button
            data-testid={KIOSK_PAGE.confirmPunchButton}
            onClick={confirmPunch}
            className={`mt-8 px-10 py-5 rounded-full text-xl font-bold text-white shadow-2xl hover:-translate-y-1 transition-all ${identified.next_event_type === "entrada" ? "bg-emerald-600" : "bg-terracotta"}`}
          >
            Confirmar {identified.next_event_type}
          </button>
          <button data-testid={KIOSK_PAGE.cancelButton} onClick={reset} className="block mx-auto mt-6 text-white/40 text-sm hover:text-white/70">Cancelar</button>
        </div>
      ) : (
        <div className="text-center">
          <p className="text-white/70 text-lg mb-6">Ingresa tu PIN o escanea tu código QR</p>
          <div className="text-white text-4xl font-mono tracking-[0.3em] mb-6 h-12">{"•".repeat(input.length) || "\u00A0"}</div>
          <div className="grid grid-cols-3 gap-3 w-64">
            {["1","2","3","4","5","6","7","8","9"].map((n) => (
              <button key={n} data-testid={KIOSK_PAGE.numpadButton} onClick={() => setInput((v) => (v + n).slice(0, 6))} className="bg-white/10 hover:bg-white/20 text-white text-2xl font-semibold rounded-2xl py-4 transition-colors">{n}</button>
            ))}
            <button data-testid={KIOSK_PAGE.clearButton} onClick={() => setInput("")} className="bg-white/5 hover:bg-white/15 text-white/60 rounded-2xl py-4 flex items-center justify-center"><Delete className="w-5 h-5" /></button>
            <button onClick={() => setInput((v) => (v + "0").slice(0, 6))} className="bg-white/10 hover:bg-white/20 text-white text-2xl font-semibold rounded-2xl py-4 transition-colors">0</button>
            <button data-testid={KIOSK_PAGE.identifyButton} onClick={() => identify(input)} className="bg-petroleum hover:bg-petroleum/80 text-white rounded-2xl py-4 flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></button>
          </div>
          <p className="text-white/30 text-xs mt-8">También puedes usar un lector de código de barras/QR — al escanear se enviará solo.</p>
        </div>
      )}
    </div>
  );
}
